"use client"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog"
import { ContractsTable } from "./contracts-table"
import type { GroupedContract, SuspensionLineDto } from "../../types/suspensions"
import { lineHasImpedimento } from "../../constants/suspensionOperationalReasons"

interface ImpedimentoListModalProps {
  open: boolean
  suspensions: SuspensionLineDto[]
  expandedContracts: Set<string>
  onToggleExpand: (contractId: string) => void
  onClose: () => void
  onViewContract: (contractId: string) => void
  onViewContractSummary: (contractId: string) => void
  onViewCredentials: (lineId: string, clientId: string) => void
  onMarkSuspended: (lineId: string) => void
  onEditImpedimento: (lineId: string) => void
  onViewSummary: (lineId: string) => void
  isAdmin: boolean
  metricsMode: "lines" | "contracts"
}

function groupImpedimentoLines(
  lines: SuspensionLineDto[],
  expandedContracts: Set<string>
): GroupedContract[] {
  const byContract = new Map<string, GroupedContract>()
  for (const line of lines) {
    let group = byContract.get(line.contractId)
    if (!group) {
      group = {
        contractId: line.contractId,
        contractNumber: line.contractNumber,
        clientName: line.clientName,
        clientCnpj: line.clientCnpj,
        expanded: expandedContracts.has(line.contractId),
        lines: [],
      }
      byContract.set(line.contractId, group)
    }
    group.lines.push({ id: line.id, type: "suspension", data: line })
  }
  return Array.from(byContract.values())
}

export function ImpedimentoListModal({
  open,
  suspensions,
  expandedContracts,
  onToggleExpand,
  onClose,
  onViewContract,
  onViewContractSummary,
  onViewCredentials,
  onMarkSuspended,
  onEditImpedimento,
  onViewSummary,
  isAdmin,
  metricsMode,
}: ImpedimentoListModalProps) {
  const impedimentoLines = suspensions.filter(lineHasImpedimento)
  const contracts = groupImpedimentoLines(impedimentoLines, expandedContracts)
  const lineCount = impedimentoLines.length
  const contractCount = contracts.length
  const unitLabel =
    metricsMode === "contracts"
      ? `${contractCount} contrato${contractCount === 1 ? "" : "s"}`
      : `${lineCount} linha${lineCount === 1 ? "" : "s"}`

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Não foi possível suspender</DialogTitle>
          <DialogDescription>
            {unitLabel} com impedimento registado — fora das métricas de urgência (1ª suspensão, urgentes, etc.).
          </DialogDescription>
        </DialogHeader>

        {contracts.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            Nenhuma linha com impedimento no filtro actual.
          </p>
        ) : (
          <ContractsTable
            contracts={contracts}
            showSuspensions
            showToSend={false}
            expandedContracts={expandedContracts}
            onToggleExpand={onToggleExpand}
            onViewContract={onViewContract}
            onViewContractSummary={onViewContractSummary}
            onViewCredentials={onViewCredentials}
            onMarkSuspended={onMarkSuspended}
            onEditImpedimento={onEditImpedimento}
            onViewSummary={onViewSummary}
            onViewAddress={() => {}}
            isAdmin={isAdmin}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
